import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BankAccountFormModel } from './bank-account-form.model';
import { KontoBankowe } from '../../../../models/konto-bankowe';
import { DomesticBankAccountFormBuilder } from '../../form-domestic-bank-account/form-group/domestic-bank-account.form.builder';
import { ForeignBankAccountFormBuilder } from '../../form-foreign-bank-account/form-group/foreign-bank-account.form.builder';
import { SharedValidators } from '../../../../validators/shared-validators';
import { BankAccountValidator } from '../../../../validators/bank-account.validator';

export class BankAccountFormBuilder {
  public static createFormGroup(
    bankAccount?: KontoBankowe,
  ): FormGroup<BankAccountFormModel> {
    const fg = new FormGroup<BankAccountFormModel>({
      isBankAccount: new FormControl(!!bankAccount?.id || false),
      isForeign: new FormControl(bankAccount?.czyZagraniczne || false),
      domesticAccount:
        DomesticBankAccountFormBuilder.createFormGroup(bankAccount),
      foreignAccount:
        ForeignBankAccountFormBuilder.createFormGroup(bankAccount),
    });
    this.#setDomesticAccountValidators(fg);
    this.#setForeignAccountValidators(fg);

    return fg;
  }

  static #setDomesticAccountValidators(
    fg: FormGroup<BankAccountFormModel>,
  ): void {
    fg.controls.domesticAccount.controls.numerKonta.addValidators([
      SharedValidators.when(Validators.required, () =>
        this.isDomesticBankAccount(fg),
      ),
      SharedValidators.when(BankAccountValidator.validBankAccountNumber, () =>
        this.isDomesticBankAccount(fg),
      ),
    ]);
    fg.controls.domesticAccount.controls.daneWlasciciela.addValidators(
      SharedValidators.when(
        Validators.required,
        () =>
          !!fg.controls.isBankAccount.value &&
          !!fg.controls.domesticAccount.controls.nieJestWlascicielem.value,
      ),
    );
  }

  static #setForeignAccountValidators(
    fg: FormGroup<BankAccountFormModel>,
  ): void {
    fg.controls.foreignAccount.controls.numerKonta.addValidators([
      SharedValidators.when(Validators.required, () =>
        this.isForeignBankAccount(fg),
      ),
      SharedValidators.when(BankAccountValidator.validUEBankAccountNumber, () =>
        this.isForeignBankAccount(fg),
      ),
      SharedValidators.when(
        BankAccountValidator.onlyBigLettersAndNumbersValidator,
        () => this.isForeignBankAccount(fg),
      ),
    ]);
    fg.controls.foreignAccount.controls.daneBanku.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.isForeignBankAccount(fg),
      ),
    );
    fg.controls.foreignAccount.controls.identyfikatorBanku.addValidators([
      SharedValidators.when(Validators.required, () =>
        this.isForeignBankAccount(fg),
      ),
      SharedValidators.when(
        BankAccountValidator.onlyBigLettersAndNumbersValidator,
        () => this.isForeignBankAccount(fg),
      ),
    ]);
  }

  static isForeignBankAccount(fg: FormGroup<BankAccountFormModel>): boolean {
    return !!fg.controls.isBankAccount.value && !!fg.controls.isForeign.value;
  }

  static isDomesticBankAccount(fg: FormGroup<BankAccountFormModel>): boolean {
    return !!fg.controls.isBankAccount.value && !fg.controls.isForeign.value;
  }

  public static toModel(
    formGroup: FormGroup<BankAccountFormModel>,
  ): KontoBankowe | null {
    if (!formGroup.controls.isBankAccount.value) {
      return null;
    }

    if (!!formGroup.controls.isForeign.value) {
      return ForeignBankAccountFormBuilder.toModel(
        formGroup.controls.foreignAccount,
      );
    } else {
      return DomesticBankAccountFormBuilder.toModel(
        formGroup.controls.domesticAccount,
      );
    }
  }
}
